// frontend/src/components/admin/BannerManagement.jsx
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import bannerService from "../../services/bannerService";
import {
  Image,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  X,
  Upload,
  Link,
  Monitor,
  Smartphone,
} from "lucide-react";

export default function BannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    link: "",
    imagem_desktop: null,
    imagem_mobile: null,
    ativo: true,
    ordem: 0,
  });
  const [imagePreviews, setImagePreviews] = useState({
    desktop: null,
    mobile: null,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    setLoading(true);
    const result = await bannerService.getAllBanners();
    if (result.success) {
      setBanners(result.data);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingBanner(null);
    setFormData({
      titulo: "",
      descricao: "",
      link: "",
      imagem_desktop: null,
      imagem_mobile: null,
      ativo: true,
      ordem: 0,
    });
    setImagePreviews({ desktop: null, mobile: null });
    setIsModalOpen(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      titulo: banner.titulo,
      descricao: banner.descricao || "",
      link: banner.link || "",
      imagem_desktop: null,
      imagem_mobile: null,
      ativo: banner.ativo,
      ordem: banner.ordem,
    });
    
    // Obtener las URLs de las imágenes existentes
    const desktopUrl = getImageUrl(banner, "imagem_desktop");
    const mobileUrl = getImageUrl(banner, "imagem_mobile");
    
    setImagePreviews({
      desktop: desktopUrl,
      mobile: mobileUrl,
    });
    
    setIsModalOpen(true);
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Guardar el archivo en formData
      setFormData({ ...formData, [type]: file });
      
      // Crear preview con FileReader
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => ({ ...prev, [type]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const getImageUrl = (banner, field) => {
    if (!banner) return null;
    
    // Primero intentar con las URLs del backend
    if (field === 'imagem_desktop' && banner.imagem_desktop_url) {
      return banner.imagem_desktop_url;
    }
    if (field === 'imagem_mobile' && banner.imagem_mobile_url) {
      return banner.imagem_mobile_url;
    }
    
    const image = banner[field];
    if (!image) return null;
    
    // Si ya es una URL completa
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }
    
    // Construir URL
    let baseUrl = '';
    if (process.env.NODE_ENV === 'development') {
      baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    } else {
      baseUrl = 'https://brechovizinho.com.br';
    }
    
    let path = image.startsWith("/") ? image : "/" + image;
    if (!path.startsWith("/storage/")) {
      path = "/storage" + path;
    }
    
    return `${baseUrl}${path}`;
  };

  // Función para eliminar una imagen
  const removeImage = (type) => {
    setImagePreviews((prev) => ({ ...prev, [type]: null }));
    setFormData({ ...formData, [type]: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titulo.trim()) {
      Swal.fire("Erro!", "O título é obrigatório.", "error");
      return;
    }

    if (!formData.imagem_desktop && !editingBanner) {
      Swal.fire("Erro!", "Selecione uma imagem para desktop.", "error");
      return;
    }

    if (!formData.imagem_mobile && !editingBanner) {
      Swal.fire("Erro!", "Selecione uma imagem para mobile.", "error");
      return;
    }

    setSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append("titulo", formData.titulo);
      formPayload.append("descricao", formData.descricao || "");
      formPayload.append("link", formData.link || "");
      formPayload.append("ativo", formData.ativo ? "1" : "0");
      formPayload.append("ordem", formData.ordem || 0);

      if (formData.imagem_desktop && formData.imagem_desktop instanceof File) {
        formPayload.append("imagem_desktop", formData.imagem_desktop);
      }
      if (formData.imagem_mobile && formData.imagem_mobile instanceof File) {
        formPayload.append("imagem_mobile", formData.imagem_mobile);
      }

      let result;
      if (editingBanner) {
        result = await bannerService.updateBanner(editingBanner.id, formPayload);
      } else {
        result = await bannerService.createBanner(formPayload);
      }

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: editingBanner ? "Banner atualizado!" : "Banner criado!",
          text: editingBanner
            ? "O banner foi atualizado com sucesso."
            : "O banner foi criado com sucesso.",
          timer: 2000,
          showConfirmButton: false,
        });
        setIsModalOpen(false);
        loadBanners();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      Swal.fire(
        "Erro!",
        error.message || "Não foi possível salvar o banner.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (banner) => {
    try {
      const result = await bannerService.toggleActive(banner.id);
      if (result.success) {
        loadBanners();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      Swal.fire(
        "Erro!",
        "Não foi possível alterar o status do banner.",
        "error"
      );
    }
  };

  const deleteBanner = async (banner) => {
    const result = await Swal.fire({
      title: "Excluir banner?",
      text: `O banner "${banner.titulo}" será removido permanentemente.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await bannerService.deleteBanner(banner.id);
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Banner excluído!",
            timer: 2000,
            showConfirmButton: false,
          });
          loadBanners();
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível excluir o banner.", "error");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#449879] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-sm text-gray-500">
            {banners.length} banner(s) cadastrado(s)
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-[#449879] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#367d64] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Banner
        </button>
      </div>

      {banners.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum banner cadastrado</p>
          <p className="text-sm text-gray-400 mt-1">
            Clique em "Novo Banner" para adicionar.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((banner) => (
            <div
              key={banner.id}
              className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                banner.ativo ? "border-gray-200" : "border-gray-200 opacity-60"
              }`}
            >
              <div className="relative h-32 bg-gray-100 overflow-hidden">
                <img
                  src={getImageUrl(banner, "imagem_desktop") || "/placeholder-image.jpg"}
                  alt={banner.titulo}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/placeholder-image.jpg";
                  }}
                />
                {!banner.ativo && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-sm font-medium bg-gray-800/80 px-3 py-1 rounded-full">
                      Inativo
                    </span>
                  </div>
                )}
                {banner.imagem_mobile && (
                  <div className="absolute top-2 left-2 bg-blue-500/80 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Smartphone className="w-3 h-3" />
                  </div>
                )}
                <div className="absolute bottom-2 right-2 flex gap-1">
                  <span className="bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Monitor className="w-2.5 h-2.5" />
                    1576×300
                  </span>
                  {banner.imagem_mobile && (
                    <span className="bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
                      <Smartphone className="w-2.5 h-2.5" />
                      807×376
                    </span>
                  )}
                </div>
              </div>

              <div className="p-4">
                <h3 className="font-semibold text-gray-800 truncate">
                  {banner.titulo}
                </h3>
                {banner.descricao && (
                  <p className="text-sm text-gray-500 truncate mt-1">
                    {banner.descricao}
                  </p>
                )}
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Link className="w-3 h-3" />
                    {banner.link ? "Link definido" : "Sem link"}
                  </span>
                  <span>Ordem: {banner.ordem}</span>
                </div>
              </div>

              <div className="border-t border-gray-100 p-3 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(banner)}
                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteBanner(banner)}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => toggleActive(banner)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    banner.ativo
                      ? "text-green-600 hover:bg-green-50"
                      : "text-gray-400 hover:bg-gray-100"
                  }`}
                  title={banner.ativo ? "Desativar" : "Ativar"}
                >
                  {banner.ativo ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingBanner ? "Editar Banner" : "Novo Banner"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) =>
                    setFormData({ ...formData, titulo: e.target.value })
                  }
                  placeholder="Ex: Black Friday no Brechó!"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879]"
                  required
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) =>
                    setFormData({ ...formData, descricao: e.target.value })
                  }
                  placeholder="Descrição do banner..."
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] resize-none"
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link (opcional)
                </label>
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) =>
                    setFormData({ ...formData, link: e.target.value })
                  }
                  placeholder="https://exemplo.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879]"
                />
              </div>

              {/* Imagen Desktop */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Monitor className="w-4 h-4 inline mr-1 text-[#449879]" />
                  Imagem Desktop <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Resolução recomendada: <strong>1576×300</strong> (proporção 5.25:1)
                </p>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-[#449879] transition-colors ${
                    imagePreviews.desktop
                      ? "border-[#449879] bg-green-50/30"
                      : "border-gray-300"
                  }`}
                  onClick={() => document.getElementById("banner-desktop").click()}
                >
                  {imagePreviews.desktop ? (
                    <div className="relative">
                      <img
                        src={imagePreviews.desktop}
                        alt="Preview Desktop"
                        className="max-h-32 mx-auto rounded-lg object-contain"
                        onError={(e) => {
                          console.error("Error loading desktop preview:", e.target.src);
                          e.target.src = "/placeholder-image.jpg";
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage("desktop");
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                      <p className="text-sm text-gray-500">Clique para selecionar</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Formatos: JPG, PNG, WEBP (max 5MB)
                      </p>
                    </>
                  )}
                  <input
                    id="banner-desktop"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "imagem_desktop")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Imagen Mobile */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Smartphone className="w-4 h-4 inline mr-1 text-[#449879]" />
                  Imagem Mobile <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Resolução recomendada: <strong>807×376</strong> (proporção 2.15:1)
                </p>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-[#449879] transition-colors ${
                    imagePreviews.mobile
                      ? "border-[#449879] bg-green-50/30"
                      : "border-gray-300"
                  }`}
                  onClick={() => document.getElementById("banner-mobile").click()}
                >
                  {imagePreviews.mobile ? (
                    <div className="relative">
                      <img
                        src={imagePreviews.mobile}
                        alt="Preview Mobile"
                        className="max-h-32 mx-auto rounded-lg object-contain"
                        onError={(e) => {
                          console.error("Error loading mobile preview:", e.target.src);
                          e.target.src = "/placeholder-image.jpg";
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage("mobile");
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                      <p className="text-sm text-gray-500">Clique para selecionar</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Formatos: JPG, PNG, WEBP (max 5MB)
                      </p>
                    </>
                  )}
                  <input
                    id="banner-mobile"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "imagem_mobile")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Ordem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordem de exibição
                </label>
                <input
                  type="number"
                  value={formData.ordem}
                  onChange={(e) =>
                    setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })
                  }
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879]"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Números menores aparecem primeiro
                </p>
              </div>

              {/* Ativo */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) =>
                      setFormData({ ...formData, ativo: e.target.checked })
                    }
                    className="w-4 h-4 text-[#449879] rounded border-gray-300 focus:ring-[#449879]"
                  />
                  <span className="text-sm text-gray-700">Banner ativo</span>
                </label>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#449879] text-white py-2.5 rounded-xl font-medium hover:bg-[#367d64] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {submitting
                    ? "Salvando..."
                    : editingBanner
                    ? "Atualizar Banner"
                    : "Criar Banner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}